import { cloneDeep, set } from "lodash";
import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import ApiStateModal from "../../../components/reusable-ui/basic-modal";
import { MESSAGE_TYPE, COMPONENT_MODES } from "../../../data/constants";
import { isAddNewMode, isEditMode } from "../../../helpers/catalog-helper";
import { getAllAccountsData, saveAccountData } from "../../../redux/slices";
import { RootState } from "../../../redux/store";
import AccountDetailsForm from "./account-details";
import AccountsViewComponent from "./accounts-view";
import "./style.scss";

const AccountsManagement: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const [accountId, setAccountId] = useState<string>("");
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMessageMap, setErrorMsgMap] = useState<any>({});
  const [accountDetails, setAccountDetails] = useState<any>({});
  const [modeConfig, setModeConfig] = useState<any>({
    //it will determine whether it's in view mode or edit mode
    type: COMPONENT_MODES.viewMode,
    tabId: "",
  });
  const accountsDataMap = useSelector(
    (state: RootState) => state.accountsReducer.accountsData
  );
  const [accountNamesList, setAccountNamesList] = useState<any>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalText, setSuccessModalText] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllAccountsData());
  }, []);

  useEffect(() => {
    if (accountsDataMap && Object.keys(accountsDataMap)?.length) {
      let dataToUpdate: any = accountsDataMap
        ? Object.values(accountsDataMap).map(
            (accountData: any, index: number) => {
              if (accountId !== accountData?.mid) {
                return accountData?.name;
              }
            }
          )
        : [];
      setAccountNamesList(dataToUpdate);
    }
  }, [accountsDataMap, accountId]);

  useEffect(() => {
    if (isAddNewMode(modeConfig.type) || !accountId) {
      setAccountDetails({});
      setErrorMsgMap({});
    }
  }, [modeConfig, accountId]);

  useEffect(() => {
    if (!isAddNewMode(modeConfig?.type) && accountId) {
      setAccountDetails(accountsDataMap[accountId]);
    }
  }, [accountId, accountsDataMap]);

  const onDiscardCtaClick = () => {
    resetStateOnClose();
    if (!isAddNewMode(modeConfig?.type)) {
      if (accountId) {
        setAccountDetails(cloneDeep(accountsDataMap[accountId]));
      } else {
        setAccountDetails({});
      }
    }
  };

  const validateAccountDetails = (accountDetails: any) => {
    if (
      accountDetails?.name &&
      accountNamesList.indexOf(accountDetails?.name) === -1
    ) {
      return { isValid: true, dataToPost: accountDetails };
    }
    let errorMsgMap: any = {};

    if (!accountDetails?.name) {
      errorMsgMap.name = "This is mandatory field";
    }

    if (
      accountDetails?.name &&
      accountNamesList.indexOf(accountDetails?.name) !== -1
    ) {
      errorMsgMap.name = "Name should be unique";
    }

    return { isValid: false, errorMsgMap };
  };

  const onSaveCtaClick = () => {
    const validatedData: any = validateAccountDetails(accountDetails);
    const callback = (response: any) => {
      if (isEditMode(modeConfig?.type)) {
        // dispatch(updateChargesList({ key: accountId, data: response }));
        setSuccessModalText("Changes Saved Successfully");
      } else if (isAddNewMode(modeConfig?.type)) {
        // dispatch(getAllAccountsData());
        setSuccessModalText("Account Created Successfully");
      }
      dispatch(getAllAccountsData());

      setShowSuccessModal(true);
      resetStateOnClose();
    };
    if (validatedData?.isValid) {
      dispatch(
        saveAccountData({
          dataToPost: accountDetails,
          callback,
        })
      );
    } else {
      setErrorMsgMap(validatedData?.errorMsgMap);
    }
  };

  const updateCurDataInParentState = (key: string, value: any) => {
    const dataToUpdate = cloneDeep(accountDetails) || {};
    set(dataToUpdate, key, value);
    setAccountDetails(dataToUpdate);
    setIsChangesMade(true);
  };

  function resetStateOnClose() {
    setErrorMsgMap({});
    setIsChangesMade(false);

    setModeConfig({ type: COMPONENT_MODES.viewMode });
  }

  function clearCurData() {
    setAccountDetails({});
  }

  const commonProps = {
    curData: accountDetails,
    modeConfig,
    setModeConfig,
    updateCurDataInParentState,
    curDataId: accountId,
    errorMessageMap,
    onSaveCtaClick,
    onDiscardCtaClick,
    isChangesMade,
    clearCurData,
  };

  return (
    <section className="flex h-[calc(100vh-141px)] overflow-hidden sm:flex-col accountsManageContainer">
      <AccountsViewComponent openDetailsView={setAccountId} {...commonProps} />

      {accountId ? <AccountDetailsForm {...commonProps} /> : null}

      {showSuccessModal && (
        <ApiStateModal
          modalType={MESSAGE_TYPE.success}
          open={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          mainText={successModalText}
        ></ApiStateModal>
      )}
    </section>
  );
};

export default AccountsManagement;
